import { useEffect, useState } from 'react';

// =============================================================================
// Light/Dark theme toggle (W8). Sits in the header next to NotificationsBell.
//
// - Default theme is LIGHT (white). prefers-color-scheme is intentionally
//   ignored — the app boots white unless the operator explicitly chose dark.
// - The choice persists in localStorage under `brick.theme` and is mirrored
//   onto <html data-theme="…">, which drives the CSS-variable palette in
//   globals.css. The same key is read by the inline bootstrap in index.html
//   so the first paint already matches the saved theme (no FOUC flash).
// =============================================================================

export const THEME_STORAGE_KEY = 'brick.theme';
type Theme = 'light' | 'dark';

/** Read the persisted theme, defaulting to 'light' when unset/invalid. */
function readStoredTheme(): Theme {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/** Apply a theme to <html> and persist it. Shared by mount + click. */
function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* storage may be unavailable (private mode) — toggle still works in-session */
  }
}

export function ThemeToggle(): JSX.Element {
  const [theme, setTheme] = useState<Theme>('light');

  // Mount: read the stored choice and reconcile <html> with it. The inline
  // bootstrap in index.html already set data-theme before React mounted, so
  // this just syncs component state (and is a no-op write).
  useEffect(() => {
    const stored = readStoredTheme();
    setTheme(stored);
    applyTheme(stored);
  }, []);

  function toggle(): void {
    setTheme((prev) => {
      const next: Theme = prev === 'dark' ? 'light' : 'dark';
      applyTheme(next);
      return next;
    });
  }

  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      // 보조기술이 읽는 이름과 툴팁이 서로 다른 말을 하던 것을 맞춘다(검수 C11).
      aria-label={isDark ? '라이트 모드로 전환' : '다크 모드로 전환'}
      aria-pressed={isDark}
      title={isDark ? '라이트 모드로 전환' : '다크 모드로 전환'}
      onClick={toggle}
      className="inline-flex h-[36px] w-[36px] items-center justify-center rounded border border-line bg-panel-2 text-text-dim transition-colors hover:border-line-strong hover:text-text"
    >
      {isDark ? <SunGlyph /> : <MoonGlyph />}
    </button>
  );
}

function SunGlyph(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="3.2" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M10 2.5v1.8M10 15.7v1.8M3.4 3.4l1.3 1.3M15.3 15.3l1.3 1.3M2.5 10h1.8M15.7 10h1.8M3.4 16.6l1.3-1.3M15.3 4.7l1.3-1.3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoonGlyph(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M16 11.2A6.2 6.2 0 018.8 4a.6.6 0 00-.83-.7 7 7 0 109.73 8.73.6.6 0 00-.7-.83Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}
